import React, { createContext, useContext, useRef } from 'react';

interface OutletContextValue {
  outlets: Record<string, React.RefObject<HTMLDivElement>>;
  registerOutlet: (id?: string) => React.RefObject<HTMLDivElement>;
  getOutlet: (id?: string) => React.RefObject<HTMLDivElement> | undefined;
}

const OutletContext = createContext<OutletContextValue | null>(null);

export function OutletProvider({ children }: { children: React.ReactNode }) {
  const outlets = useRef<Record<string, React.RefObject<HTMLDivElement>>>({});
  const firstOutlet = useRef<React.RefObject<HTMLDivElement> | null>(null);

  const registerOutlet = (id?: string) => {
    if (!id) {
      if (!firstOutlet.current) {
        firstOutlet.current = React.createRef();
      }
      return firstOutlet.current;
    }
    if (!outlets.current[id]) {
      outlets.current[id] = React.createRef();
    }
    return outlets.current[id];
  };

  const getOutlet = (id?: string) => {
    if (!id) {
      return firstOutlet.current || undefined;
    }
    return outlets.current[id];
  };

  return (
    <OutletContext.Provider
      value={{ outlets: outlets.current, registerOutlet, getOutlet }}
    >
      {children}
    </OutletContext.Provider>
  );
}

export function useOutletContext() {
  const context = useContext(OutletContext);
  if (!context) {
    throw new Error('useOutletContext must be used within an OutletProvider');
  }
  return context;
}
